import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { request } from './helper';
import type { AppDispatch } from '@/store/store';
import { SavePaymentMethodRequest, Plan, BillingState } from '@/types';
import { setMeData } from "@/store/auth";

// **Initial State**
const initialState: BillingState = {
  intent: {},
  plans: [],
  status: 'idle',
  subscriptionStatus: 'idle',
};
type SuccessResponse<T> = T | null; // Type for success or null on failure

// **Thunks**
export const billingSetupIntent = createAsyncThunk<
  SuccessResponse<any>, // Return type
  void, // No arguments
  { rejectValue: string }
>('billing/setupIntent', async (_, { rejectWithValue }) => {
  const response = await request(`api/setup-intent`);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to setup intent');
});

export const billingSavePaymentMethod = createAsyncThunk<
  SuccessResponse<any>, // Return type
  SavePaymentMethodRequest, // Request payload
  { rejectValue: string }
>('billing/savePaymentMethod', async (data, { rejectWithValue }) => {
  const response = await request(`api/save-payment-method`, 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to save payment method');
});

export const billingSubscribe = createAsyncThunk<
  SuccessResponse<any>,
  void,
  { rejectValue: string, dispatch: AppDispatch }
>('billing/subscribe', async (_, { rejectWithValue, dispatch }) => {
  const response = await request(`api/subscribe`);  
  dispatch(setMeData(response.data))
  return response.success ? response.data : rejectWithValue(response.error || 'Subscription failed');
});

export const billingCreatePlan = createAsyncThunk<
  SuccessResponse<any>,
  Plan,
  { rejectValue: string }
>('billing/createPlan', async (data, { rejectWithValue }) => {
  const response = await request(`admin/plan`, 'POST', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to create plan');
});

export const billingUpdatePlan = createAsyncThunk<
  SuccessResponse<any>,
  { id: number; data: Partial<Plan> },
  { rejectValue: string }
>('billing/updatePlan', async ({ id, data }, { rejectWithValue }) => {
  const response = await request(`admin/plan/${id}`, 'PUT', data);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to update plan');
});

export const billingGetPlans = createAsyncThunk<
  any[],
  void,
  { rejectValue: string }
>(
  'billing/getPlans',
  async (_, { rejectWithValue }) => {
    const response = await request('admin/plan');

    // Ensure the response is a valid array, otherwise default to an empty array
    if (response.success) {
      return Array.isArray(response.data) ? response.data : [];
    } else {
      return rejectWithValue(response.error || 'Failed to fetch plans');
    }
  }
);

export const billingDeletePlan = createAsyncThunk<
  SuccessResponse<number>, // Return deleted plan ID
  number, // Plan ID as argument
  { rejectValue: string }
>('billing/deletePlan', async (id, { rejectWithValue }) => {
  const response = await request(`admin/plan/${id}`, 'DELETE');
  return response.success ? id : rejectWithValue(response.error || 'Failed to delete plan');
});

export const billingUnsubscribe = createAsyncThunk<
  SuccessResponse<any>,
  void,
  { rejectValue: string }
>('billing/unsubscribe', async (_, { rejectWithValue }) => {
  const response = await request(`api/unsubscribe`);
  return response.success ? response.data : rejectWithValue(response.error || 'Unsubscription failed');
});

export const billingUnsubscribeUserByAdmin = createAsyncThunk<
  SuccessResponse<any>,
  number, // User ID
  { rejectValue: string }
>('billing/unsubscribeUserByAdmin', async (id, { rejectWithValue }) => {
  const response = await request(`admin/unsubscribe/${id}`);
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to unsubscribe user');
});

  export const billingSaveIntegration = createAsyncThunk<
  SuccessResponse<any>,
  { client_id: string; client_secret: string },
  { rejectValue: string }
>('billing/saveIntegration', async (data, { rejectWithValue, dispatch }) => {
  const response = await request(`api/save-integration`, 'POST', data);
  dispatch(setMeData(response.data))
  return response.success ? response.data : rejectWithValue(response.error || 'Failed to save integration');
});

// **Slice**
export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    removeAndUpdatePlansList: (state, { payload }: PayloadAction<{ id: number; current: boolean }>) => {
      state.plans = state.plans.filter((plan) => plan.id !== payload.id);
      if (payload.current && state.plans.length) {
        state.plans[0].current = 1;
      }
    },
    clearSubscriptionSuccessStatus: (state) => {
      state.subscriptionStatus = 'idle';
    },
    resetIntent: (state) => {
      state.intent = {};
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state: BillingState) => {
      state.status = 'loading';
    };
    const setFailed = (state: BillingState) => {
      state.status = 'failed';
    };

    // Setup Intent
    builder
      .addCase(billingSetupIntent.pending, setLoading)
      .addCase(billingSetupIntent.fulfilled, (state, { payload }) => {
        state.intent = payload;
        state.status = 'success';
      })
      .addCase(billingSetupIntent.rejected, setFailed);

    // Save Payment Method
    builder
      .addCase(billingSavePaymentMethod.pending, setLoading)
      .addCase(billingSavePaymentMethod.fulfilled, (state) => {
        state.status = 'success';
      })
      .addCase(billingSavePaymentMethod.rejected, setFailed);

    // Subscribe
    builder
      .addCase(billingSubscribe.pending, (state) => {
        state.subscriptionStatus = 'loading';
      })
      .addCase(billingSubscribe.fulfilled, (state, { payload }) => {
        state.subscriptionStatus = 'success';
      })
      .addCase(billingSubscribe.rejected, (state) => {
        state.subscriptionStatus = 'failed';
      });

    // Get Plans
    builder
      .addCase(billingGetPlans.pending, setLoading)
      .addCase(billingGetPlans.fulfilled, (state, { payload }) => {
        state.status = 'success';
        state.plans = payload;
      })
      .addCase(billingGetPlans.rejected, setFailed);

    // Create Plan
    builder.addCase(billingCreatePlan.fulfilled, (state, { payload }) => {
      if (payload.current) {
        state.plans = state.plans.map((plan) => ({ ...plan, current: 0 }));
      }
      state.plans.push(payload);
    });

    // Update Plan
    builder.addCase(billingUpdatePlan.fulfilled, (state, { payload }) => {
      state.plans = state.plans.map((plan) => {
        if (payload.current) plan.current = 0;
        return plan.id === payload.id ? { ...plan, ...payload } : plan;
      });
    });

    // Delete Plan
    builder.addCase(billingDeletePlan.fulfilled, (state, { payload }) => {
      state.plans = state.plans.filter((plan) => plan.id !== payload);
    });

    // Unsubscribe
    builder
      .addCase(billingUnsubscribe.pending, (state) => {
        state.subscriptionStatus = 'loading';
      })
      .addCase(billingUnsubscribe.fulfilled, (state, { payload }) => {
        state.subscriptionStatus = 'success';
        // state.me = payload;
      })
      .addCase(billingUnsubscribe.rejected, (state) => {
        state.subscriptionStatus = 'failed';
      });

    // Unsubscribe User By Admin
    builder.addCase(billingUnsubscribeUserByAdmin.fulfilled, () => {
      // Optional: logic for admin unsubscribe success
    });
  },
});

// **Export Actions**
export const { removeAndUpdatePlansList, clearSubscriptionSuccessStatus, resetIntent } = billingSlice.actions;

// **Export Reducer**
export default billingSlice.reducer;